/**
 * useHoverPrefetch — desktop-only "warm the SW cache on hover" helper.
 *
 * When a client's mouse hovers a grid thumbnail for 150ms, we silently
 * kick off a fetch for the LIGHTBOX-sized resize of that same photo
 * (`?w=2000&q=88`). The fetch gets intercepted by the Service Worker's
 * `IMG_CACHE` handler and stored persistently. By the time the client
 * actually clicks, the full-view bytes are already local → lightbox
 * pops open instantly instead of blurring in.
 *
 * Returns event handlers ready to spread onto a grid tile:
 *   const hover = useHoverPrefetch(photo.url);
 *   <div {...hover}>…</div>
 *
 * Skipped on touch devices (no meaningful hover intent) and when the
 * url is empty or absolute (external CDN) since our SW only caches
 * `/api/files/{id}?w=…` responses.
 */
import { useCallback, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HOVER_DELAY_MS = 150;

// Match the exact URL PremiumLightbox uses (`?w=2000&q=88`) so a
// prefetch-then-open pair scores a 100% SW cache hit.
function buildLightboxUrl(url) {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("http")) return null; // external CDNs bypass our proxy
  return `${BACKEND}${url}${url.includes("?") ? "&" : "?"}w=2000&q=88`;
}

// Coarse-pointer devices don't have a real hover intent — skip.
const isTouchDevice = typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(hover: none), (pointer: coarse)").matches;

export default function useHoverPrefetch(photoUrl) {
  const timerRef = useRef(null);

  const onMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    const target = buildLightboxUrl(photoUrl);
    if (!target) return;
    // Delay: brief pass-throughs (dragging cursor over the grid) don't
    // kick off a prefetch storm. Only intent-ful hovers survive.
    timerRef.current = setTimeout(() => {
      const img = new Image();
      img.decoding = "async";
      img.src = target;
      timerRef.current = null;
    }, HOVER_DELAY_MS);
  }, [photoUrl]);

  const onMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { onMouseEnter, onMouseLeave };
}
